import type { Dispatch } from 'react';
import { useState, useMemo, useEffect } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import {
	useMutationInterop,
	useQueryInterop,
	type MutationFunctionInterop as MutationFunction,
} from '@confluence/apollo-relay-interop';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';

import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';
import { NO_CLASSIFICATION_OPTION_ID } from '../constants/NoClassification.ts';
import { FlagStatus } from '../constants/FlagStatus.ts';
import { Status } from '../constants/Status.ts';
import type { ErrorState } from '../constants/ErrorState.ts';
import { getQuerySingleContentStatusArrayVariable } from '../utils/getQuerySingleContentStatusArrayVariable.ts';
import { getReadAndWriteContextFromQueryContext } from '../utils/getReadAndWriteContextFromQueryContext.ts';

import type {
	useContentClassificationContentDataClassificationQuery$data,
	useContentClassificationContentDataClassificationQuery$variables,
} from './__generated__/useContentClassificationContentDataClassificationQuery.graphql.ts';
import type {
	useContentClassificationV2ContentDataClassificationQuery$data,
	useContentClassificationV2ContentDataClassificationQuery$variables,
} from './__generated__/useContentClassificationV2ContentDataClassificationQuery.graphql.ts';
import type {
	useContentClassification_content$data,
	useContentClassification_content$key,
} from './__generated__/useContentClassification_content.graphql.ts';
import type {
	useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
	useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables,
} from './__generated__/useContentClassificationBulkUpdateContentDataClassificationLevelMutation.graphql.ts';
import type {
	useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
	useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables,
} from './__generated__/useContentClassificationBulkDeleteContentDataClassificationLevelMutation.graphql.ts';
import { useClassificationErrorState } from './useClassificationErrorState.ts';
import UseContentClassificationV2ContentDataClassificationQuery from './__generated__/useContentClassificationV2ContentDataClassificationQuery.graphql.ts';

// Control: uses classificationLevel (old field)
export const useContentClassification_content: GraphQLTaggedNode = graphql`
	fragment useContentClassification_content on Content
	@argumentDefinitions(useClassificationLevelV2: { type: "Boolean!", defaultValue: false }) {
		id
		classificationLevelDetails {
			classificationLevel @skip(if: $useClassificationLevelV2) {
				id
				guideline
				guidelineADF
				description
				color
				name
				status
			}
			classificationLevelV2 @include(if: $useClassificationLevelV2) {
				id
				guideline
				guidelineADF
				description
				color
				name
				status
			}
			featureEnabled
			source
			isReclassificationPermitted
			canManageSpaceSettings
			classificationControlSetting
			userOverrideSetting
		}
	}
`;

// Control query: uses classificationLevel (old field)
const UseContentClassificationContentDataClassificationQuery = graphql`
	query useContentClassificationContentDataClassificationQuery(
		$contentId: ID!
		$lowerCaseContentStatusArray: [String]
	) {
		singleContent(id: $contentId, status: $lowerCaseContentStatusArray) {
			id
			classificationLevelDetails {
				classificationLevel {
					id
					guideline
					guidelineADF
					description
					color
					name
					status
				}
				featureEnabled
				source
				isReclassificationPermitted
				canManageSpaceSettings
				classificationControlSetting
				userOverrideSetting
			}
		}
	}
`;

/**
 * Return type for useContentClassification hook. Includes:
 * 1) Content's classification level by contentId & by spaceId,
 * 2) Mutation functions for updating & deleting the content's classification,
 * 3) Flag status for rendering error flag upon updating classification.
 */
export type ContentClassificationMetadata = {
	classification: ClassificationLevel | null;
	spaceDefaultIsOverridden: boolean;
	errorState: ErrorState;
	contentLevelError?: ApolloErrorInterop;
	shouldDisplayClassification: boolean;
	updateContentDataClassificationLevel: MutationFunction<
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables
	>;
	deleteContentDataClassificationLevel: MutationFunction<
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables
	>;
	setSubmittedClassification: Dispatch<ClassificationLevel | null>;
	flagStatus: FlagStatus;
	setFlagStatus: Dispatch<FlagStatus>;
	hasNoClassification?: boolean;
	contentLevelLoading?: boolean;
	contentStatusContext: ClassificationQueryContext;
	featureEnabled?: boolean;
	source?: string | null;
	isReclassificationPermitted?: boolean;
	canManageSpaceSettings?: boolean;
	classificationControlSetting?: string | null;
	userOverrideSetting?: string | null;
};

// Note: this behavior is extracted into its own hook to allow for easier testing
interface UseSyncSubmittedClassificationOnFlagChangeI {
	flagStatus: FlagStatus;
	submittedClassification?: ClassificationLevel | null;
	setClassification: Dispatch<ClassificationLevel | null>;
	setFlagStatus: Dispatch<FlagStatus>;
}

export const useSyncSubmittedClassificationOnFlagChange = ({
	flagStatus,
	submittedClassification,
	setClassification,
	setFlagStatus,
}: UseSyncSubmittedClassificationOnFlagChangeI): void => {
	// Updates the classification state with the value of submittedClassification
	// on flag success, ensuring consistent behavior across edit tag & edit modal
	useEffect(() => {
		if (flagStatus === FlagStatus.SUCCESS) {
			setClassification(submittedClassification ?? null);
			setFlagStatus(FlagStatus.NONE);
		}
	}, [flagStatus, submittedClassification, setClassification, setFlagStatus]);
};

/**
 * Determines the context of the ContentStatus for which the content classification is read and written.
 * 'draft-only' reads and writes to 'draft',
 * 'current-only' reads and writes to 'current',
 * 'current-and-draft' reads from 'current' and writes to 'current' & 'draft' (live-pages)
 */
export type ClassificationQueryContext = 'draft-only' | 'current-only' | 'current-and-draft';

type useContentClassificationType = {
	contentId?: string;
	spaceKeyOverride?: string | null;
	shouldSkipQueries?: boolean;
	contentStatusContext?: ClassificationQueryContext;
	shouldPreloadData?: boolean;
	shouldReadFromQueries?: boolean;
};

type ClassificationLevelDetailsData =
	| NonNullable<
			useContentClassificationContentDataClassificationQuery$data['singleContent']
	  >['classificationLevelDetails']
	| NonNullable<
			useContentClassificationV2ContentDataClassificationQuery$data['singleContent']
	  >['classificationLevelDetails']
	| useContentClassification_content$data['classificationLevelDetails'];

type UseContentClassificationMetadataType = Pick<
	useContentClassificationType,
	'contentId' | 'contentStatusContext' | 'shouldReadFromQueries'
> & {
	useClassificationLevelV2: boolean;
	classificationLevelDetails?: ClassificationLevelDetailsData;
	contentLevelLoading?: boolean;
	contentLevelError?: ApolloErrorInterop;
};

/**
 *
 * @param contentId
 * @param spaceKeyOverride
 * @param contentStatusContext
 * @param shouldSkipQueries
 * @returns
 * 1) Content's classification level by contentId & by spaceId,
 * 2) Mutation functions for updating & deleting the content's classification,
 * 3) Flag status for rendering error flag upon updating classification.
 */
const useContentClassificationMetadata = ({
	contentId,
	contentStatusContext = 'current-only',
	shouldReadFromQueries = false,
	useClassificationLevelV2,
	classificationLevelDetails,
	contentLevelLoading = false,
	contentLevelError,
}: UseContentClassificationMetadataType): ContentClassificationMetadata => {
	const [flagStatus, setFlagStatus] = useState(FlagStatus.NONE);
	const [classification, setClassification] = useState<ClassificationLevel | null>(null);
	const [currentContentId, setCurrentContentId] = useState<string | undefined>(undefined);
	const [submittedClassification, setSubmittedClassification] =
		useState<ClassificationLevel | null>();

	const renderServerPlaceholder = useIsSSROrHydrating();

	const { readContext: contentStatusReadContext } =
		getReadAndWriteContextFromQueryContext(contentStatusContext);

	useSyncSubmittedClassificationOnFlagChange({
		flagStatus,
		submittedClassification,
		setClassification,
		setFlagStatus,
	});

	// The following two GQL write operations are returned by this hook, behavior upon completion is handled within the hook
	const [updateContentDataClassificationLevel] = useMutationInterop<
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
		useContentClassificationBulkUpdateContentDataClassificationLevelMutation$variables
	>(
		graphql`
			mutation useContentClassificationBulkUpdateContentDataClassificationLevelMutation(
				$id: Long!
				$contentStatuses: [ContentDataClassificationMutationContentStatus]!
				$classificationLevelId: ID!
			) {
				bulkUpdateContentDataClassificationLevel(
					input: {
						id: $id
						contentStatuses: $contentStatuses
						classificationLevelId: $classificationLevelId
					}
				) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			awaitRefetchQueries: true,
			onError: (updateClassificationError: ApolloErrorInterop) => {
				setFlagStatus(FlagStatus.ERROR);
				markErrorAsHandled(updateClassificationError);
			},
			onCompleted: (
				data: useContentClassificationBulkUpdateContentDataClassificationLevelMutation$data,
			) => {
				if (data?.bulkUpdateContentDataClassificationLevel?.success) {
					setFlagStatus(FlagStatus.SUCCESS);
				}
			},
			refetchQueries: () => [
				{
					query: useClassificationLevelV2
						? UseContentClassificationV2ContentDataClassificationQuery
						: UseContentClassificationContentDataClassificationQuery,
					variables: {
						contentId,
						lowerCaseContentStatusArray:
							getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
					},
				},
			],
		},
	);

	const [deleteContentDataClassificationLevel, { loading: deleteLoading }] = useMutationInterop<
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
		useContentClassificationBulkDeleteContentDataClassificationLevelMutation$variables
	>(
		graphql`
			mutation useContentClassificationBulkDeleteContentDataClassificationLevelMutation(
				$id: Long!
				$contentStatuses: [ContentDataClassificationMutationContentStatus]!
			) {
				bulkDeleteContentDataClassificationLevel(
					input: { id: $id, contentStatuses: $contentStatuses }
				) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			awaitRefetchQueries: true,
			onError: (deleteClassificationError: ApolloErrorInterop) => {
				setFlagStatus(FlagStatus.ERROR);
				markErrorAsHandled(deleteClassificationError);
			},
			onCompleted: (
				data: useContentClassificationBulkDeleteContentDataClassificationLevelMutation$data,
			) => {
				if (data?.bulkDeleteContentDataClassificationLevel?.success) {
					setFlagStatus(FlagStatus.SUCCESS);
				}
			},
			refetchQueries: () => [
				{
					query: useClassificationLevelV2
						? UseContentClassificationV2ContentDataClassificationQuery
						: UseContentClassificationContentDataClassificationQuery,
					variables: {
						contentId,
						lowerCaseContentStatusArray:
							getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
					},
				},
			],
		},
	);

	// Branch on the gate to narrow into the correct union member without an unsafe cast.
	// classificationLevelV2 Branch 4 returns an empty shell object (id: "") when the service
	// finds no level for a given ID — treat this as "no classification" (same as null).
	const rawClassificationLevel = useClassificationLevelV2
		? (
				classificationLevelDetails as NonNullable<
					useContentClassificationV2ContentDataClassificationQuery$data['singleContent']
				>['classificationLevelDetails']
			)?.classificationLevelV2
		: (
				classificationLevelDetails as NonNullable<
					useContentClassificationContentDataClassificationQuery$data['singleContent']
				>['classificationLevelDetails']
			)?.classificationLevel;
	const contentClassification =
		rawClassificationLevel?.id === '' ? null : (rawClassificationLevel ?? null);

	const spaceDefaultIsOverridden = classificationLevelDetails?.source === 'CONTENT';

	const errorState = useClassificationErrorState(classification, undefined, contentLevelError);

	// Determine whether to set classification either content level classification, or space default classification, or null through a series of booleans.

	// Cases where we should not display content classification:
	// 1. If "No classification" option is selected as content classification
	// 2. If content classification was archived
	// 3. If classification value from state hook is null (i.e. classification was deleted) and delete GQL call is still loading
	// 4. If delete was successful and classification value from state hook is null
	// 5. If there was an error fetching content level classification
	const shouldNotDisplayContentClassification =
		contentClassification?.id === NO_CLASSIFICATION_OPTION_ID ||
		contentClassification?.status === Status.ARCHIVED ||
		(classification === null && deleteLoading) ||
		Boolean(contentLevelError);

	// This ternary determines whether to display content classification value from the GQL response or from state hook.
	// Checks whether content ID has changed since the last render, and if it has then we display the classification value from query.
	// (Resolves bug where classification wasn't properly updating when navigating between pages via page tree)
	const contentClassificationFromQueryOrState = shouldReadFromQueries
		? contentClassification
		: currentContentId === contentId && !!classification
			? classification
			: contentClassification;

	// Ultimately, we'll be displaying one of the two values as a content's current classification (in order of priority):
	// 1. Content level classification
	// 2. No classification (may render Add Classification button instead)
	const updatedClassification = shouldNotDisplayContentClassification
		? null
		: contentClassificationFromQueryOrState;

	useEffect(() => {
		if (updatedClassification !== classification) {
			setClassification((updatedClassification as ClassificationLevel) ?? null);
			setCurrentContentId(contentId);
		}
	}, [updatedClassification, classification, contentId]);

	const isClassificationPublished =
		classification?.status === Status.PUBLISHED ||
		updatedClassification?.status === Status.PUBLISHED;

	const classificationFeatureEnabled = Boolean(classificationLevelDetails?.featureEnabled);

	const shouldDisplayClassification =
		classificationFeatureEnabled &&
		(!!classification || !!updatedClassification) &&
		!contentLevelLoading &&
		isClassificationPublished;

	const hasNoClassification = !contentLevelLoading && !classification && !updatedClassification;

	const isReclassificationPermitted =
		(classificationLevelDetails?.isReclassificationPermitted ?? false) ||
		!expVal('dlp_auto_classifications_enabled', 'isEnabled', false);

	//useEffect is not run during SSR, so we can't use classification from the state.
	//we cant use setClassification either as it causes side effects which causes unnecessary re-renders.
	//Instead, we can use updatedClassification for initial load for SSR.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSRedWithClassification = renderServerPlaceholder && updatedClassification;

	const classificationValue = isSSRedWithClassification
		? (updatedClassification ?? null)
		: (classification ?? updatedClassification ?? null);

	return useMemo<ContentClassificationMetadata>(
		() => ({
			classification: (classificationValue as ClassificationLevel) ?? null,
			spaceDefaultIsOverridden,
			contentLevelLoading,
			contentLevelError,
			updateContentDataClassificationLevel,
			deleteContentDataClassificationLevel,
			setSubmittedClassification,
			flagStatus,
			setFlagStatus,
			errorState,
			shouldDisplayClassification,
			hasNoClassification,
			contentStatusContext,
			featureEnabled: classificationFeatureEnabled,
			isReclassificationPermitted,
			source: classificationLevelDetails?.source,
			canManageSpaceSettings: classificationLevelDetails?.canManageSpaceSettings,
			classificationControlSetting: classificationLevelDetails?.classificationControlSetting,
			userOverrideSetting: classificationLevelDetails?.userOverrideSetting,
		}),
		[
			classificationValue,
			spaceDefaultIsOverridden,
			contentLevelLoading,
			contentLevelError,
			updateContentDataClassificationLevel,
			deleteContentDataClassificationLevel,
			setSubmittedClassification,
			flagStatus,
			setFlagStatus,
			errorState,
			shouldDisplayClassification,
			hasNoClassification,
			contentStatusContext,
			classificationFeatureEnabled,
			isReclassificationPermitted,
			classificationLevelDetails,
		],
	);
};

export const useContentClassification = ({
	contentId,
	contentStatusContext = 'current-only',
	shouldSkipQueries = false,
	shouldPreloadData = false,
	shouldReadFromQueries = false,
}: useContentClassificationType): ContentClassificationMetadata => {
	let useClassificationLevelV2 = false;
	if (expValEquals('confluence_classification_level_v2', 'isEnabled', true)) {
		useClassificationLevelV2 = true;
	}
	const shouldPreloadContentClassificationData = window.__SSR_RENDERED__ || shouldPreloadData;

	const { readContext: contentStatusReadContext } =
		getReadAndWriteContextFromQueryContext(contentStatusContext);

	const shouldSkipQueriesForClassification = shouldSkipQueries || !contentId;

	const queryVariables = {
		contentId: contentId ?? '',
		lowerCaseContentStatusArray: getQuerySingleContentStatusArrayVariable(contentStatusReadContext),
	};
	const queryOptions: {
		skip: boolean;
		errorPolicy: 'all';
		fetchPolicy: 'cache-first' | 'network-only';
	} = {
		skip: shouldSkipQueriesForClassification,
		errorPolicy: 'all',
		fetchPolicy: shouldPreloadContentClassificationData ? 'cache-first' : 'network-only',
	};

	const {
		data: objectLevelDataV1,
		loading: objectLevelLoadingV1,
		error: objectLevelErrorV1,
	} = useQueryInterop<
		useContentClassificationContentDataClassificationQuery$data,
		useContentClassificationContentDataClassificationQuery$variables
	>(UseContentClassificationContentDataClassificationQuery, {
		variables: queryVariables,
		...queryOptions,
		skip: shouldSkipQueriesForClassification || useClassificationLevelV2,
	});

	const {
		data: objectLevelDataV2,
		loading: objectLevelLoadingV2,
		error: objectLevelErrorV2,
	} = useQueryInterop<
		useContentClassificationV2ContentDataClassificationQuery$data,
		useContentClassificationV2ContentDataClassificationQuery$variables
	>(UseContentClassificationV2ContentDataClassificationQuery, {
		variables: queryVariables,
		...queryOptions,
		skip: shouldSkipQueriesForClassification || !useClassificationLevelV2,
	});

	const objectLevelData = useClassificationLevelV2 ? objectLevelDataV2 : objectLevelDataV1;
	const objectLevelLoading = useClassificationLevelV2 ? objectLevelLoadingV2 : objectLevelLoadingV1;
	const objectLevelError = useClassificationLevelV2 ? objectLevelErrorV2 : objectLevelErrorV1;

	return useContentClassificationMetadata({
		contentId,
		contentStatusContext,
		shouldReadFromQueries,
		useClassificationLevelV2,
		classificationLevelDetails: objectLevelData?.singleContent?.classificationLevelDetails,
		contentLevelLoading: objectLevelLoading,
		contentLevelError: objectLevelError,
	});
};

export const useContentClassificationFromFragment = ({
	contentId,
	contentFragmentRef,
	contentStatusContext = 'current-only',
	shouldReadFromQueries = false,
}: Pick<
	useContentClassificationType,
	'contentId' | 'contentStatusContext' | 'shouldReadFromQueries'
> & {
	contentFragmentRef: useContentClassification_content$key;
}): ContentClassificationMetadata => {
	const content = useFragment(useContentClassification_content, contentFragmentRef);

	return useContentClassificationMetadata({
		contentId,
		contentStatusContext,
		shouldReadFromQueries,
		useClassificationLevelV2: expValEquals('confluence_classification_level_v2', 'isEnabled', true),
		classificationLevelDetails: content?.classificationLevelDetails,
	});
};
